import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fade,
  useTheme,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentsDialog from '../../Pages/CheckoutPage/dialog';
import SquareCashAppPay from '../SquareCashAppPay/SquareCashAppPay';
import SquareApplePay from '../SquareApplePay/SquareApplePay';
import SquareGooglePay from '../SquareGooglePay/SquareGooglePay';
import { environment } from '../../../environments/environment';

// Memoized wrapper for Cash App Pay to prevent unnecessary re-renders
const MemoizedCashAppPay = memo(
  React.forwardRef(({ orderTotal, onToken, onError, currentStoreId, user, isVisible }, ref) => {
    if (!isVisible) return null;

    return (
      <Fade in={isVisible} timeout={300}>
        <Box>
          <SquareCashAppPay
            ref={ref}
            orderTotal={orderTotal}
            onToken={onToken}
            onError={onError}
            currentStoreId={currentStoreId || user?.defaultStore}
          />
        </Box>
      </Fade>
    );
  }),
  (prevProps, nextProps) => {
    // Only re-render if these specific props change
    return (
      prevProps.orderTotal === nextProps.orderTotal &&
      prevProps.currentStoreId === nextProps.currentStoreId &&
      prevProps.isVisible === nextProps.isVisible &&
      prevProps.onToken === nextProps.onToken &&
      prevProps.onError === nextProps.onError
    );
  }
);
MemoizedCashAppPay.displayName = 'MemoizedCashAppPay';

// Memoized wrapper for Apple Pay
const MemoizedApplePay = memo(
  React.forwardRef(({ orderTotal, onToken, onError, currentStoreId, user, isVisible }, ref) => {
    if (!isVisible) return null;

    return (
      <Fade in={isVisible} timeout={300}>
        <Box>
          <SquareApplePay
            ref={ref}
            orderTotal={orderTotal}
            onToken={onToken}
            onError={onError}
            currentStoreId={currentStoreId || user?.defaultStore}
          />
        </Box>
      </Fade>
    );
  }),
  (prevProps, nextProps) => {
    return (
      prevProps.orderTotal === nextProps.orderTotal &&
      prevProps.currentStoreId === nextProps.currentStoreId &&
      prevProps.isVisible === nextProps.isVisible &&
      prevProps.onToken === nextProps.onToken &&
      prevProps.onError === nextProps.onError
    );
  }
);
MemoizedApplePay.displayName = 'MemoizedApplePay';

// Google Pay component with proper hook usage
const GooglePayComponent = React.forwardRef(
  ({ orderTotal, onToken, onError, currentStoreId, user, handleGooglePaySuccess, isVisible }, ref) => {
    // Wrap onToken to include handleGooglePaySuccess - hooks must be called unconditionally
    const wrappedOnToken = React.useCallback(
      (token) => {
        if (onToken) {
          onToken(token);
        }
        if (handleGooglePaySuccess) {
          handleGooglePaySuccess(token);
        }
      },
      [onToken, handleGooglePaySuccess]
    );

    if (!isVisible) return null;

    return (
      <Fade in={isVisible} timeout={300}>
        <Box>
          <SquareGooglePay
            ref={ref}
            orderTotal={orderTotal}
            onToken={wrappedOnToken}
            onError={onError}
            currentStoreId={currentStoreId || user?.defaultStore}
          />
        </Box>
      </Fade>
    );
  }
);
GooglePayComponent.displayName = 'GooglePayComponent';

// Memoized wrapper for Google Pay
const MemoizedGooglePay = memo(GooglePayComponent, (prevProps, nextProps) => {
  return (
    prevProps.orderTotal === nextProps.orderTotal &&
    prevProps.currentStoreId === nextProps.currentStoreId &&
    prevProps.isVisible === nextProps.isVisible &&
    prevProps.onToken === nextProps.onToken &&
    prevProps.onError === nextProps.onError &&
    prevProps.handleGooglePaySuccess === nextProps.handleGooglePaySuccess
  );
});
MemoizedGooglePay.displayName = 'MemoizedGooglePay';

const PaymentMethodDialog = ({
  open,
  onClose,
  onConfirm,
  onShowLoading,
  paymentMethod,
  setPaymentMethod,
  sourceId,
  setSourceId,
  orderType,
  cards,
  setCards,
  handleChange,
  onSelect,
  change,
  openCredit,
  setCredit,
  setSelectedCard,
  cash,
  setCash,
  squarePaymentRef,
  savedCards,
  selectedSavedCardId,
  onSavedCardSelect,
  shouldSaveCard,
  onSaveCardChange,
  useNewCard,
  onUseNewCardChange,
  onCardOptionSelect,
  cashAppPayRef,
  applePayRef,
  googlePayRef,
  apiPricingData,
  storeIdD,
  user,
  handleGooglePaySuccess,
  err,
  setErr,
}) => {
  const [localPaymentMethod, setLocalPaymentMethod] = useState(paymentMethod || '');
  const [localSourceId, setLocalSourceId] = useState(sourceId || null);
  const [cardAccordionExpanded, setCardAccordionExpanded] = useState(false);
  const [isDialogReady, setIsDialogReady] = useState(false);
  const [isCardFormReady, setIsCardFormReady] = useState(false);
  const cashAppContainerRef = useRef(null);
  const applePayContainerRef = useRef(null);
  const googlePayContainerRef = useRef(null);

  // Responsive design hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Sync local state with props when dialog opens
  useEffect(() => {
    if (open) {
      setLocalPaymentMethod(paymentMethod || '');
      setLocalSourceId(sourceId || null);
      setCardAccordionExpanded(false);
      setIsCardFormReady(false);
      
      // Pre-check if Square SDK is already loaded to reduce delay
      // If SDK is loaded, we can render immediately
      if (window.Square) {
        // SDK already loaded - render immediately
        setIsDialogReady(true);
      } else {
        // SDK not loaded - use minimal delay for DOM readiness
        // Use requestAnimationFrame for faster DOM readiness check
        const frameId = requestAnimationFrame(() => {
          // Double RAF ensures layout is complete
          requestAnimationFrame(() => {
            setIsDialogReady(true);
          });
        });
        return () => cancelAnimationFrame(frameId);
      }
    } else {
      setIsDialogReady(false);
      setIsCardFormReady(false);
    }
  }, [open, paymentMethod, sourceId]);

  // Prevent container clicks from triggering payment - only allow button clicks
  useEffect(() => {
    if (!isDialogReady || !open) return;

    const handleContainerClick = (e, containerRef, squareContainerId) => {
      if (!containerRef.current) return;
      
      const target = e.target;
      // Check if click is on the Square SDK container or a button
      const isSquareButton = target.closest(`#${squareContainerId}`) ||
                            target.closest('button') ||
                            target.closest('[role="button"]') ||
                            target.tagName === 'BUTTON';
      
      // If click is not on a button, prevent it
      if (!isSquareButton && containerRef.current.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    const cashAppHandler = (e) => handleContainerClick(e, cashAppContainerRef, 'sq-cash-app-pay-container');
    const applePayHandler = (e) => handleContainerClick(e, applePayContainerRef, null);
    const googlePayHandler = (e) => handleContainerClick(e, googlePayContainerRef, 'sq-google-pay-container');

    const cashAppContainer = cashAppContainerRef.current;
    const applePayContainer = applePayContainerRef.current;
    const googlePayContainer = googlePayContainerRef.current;

    if (cashAppContainer) {
      cashAppContainer.addEventListener('click', cashAppHandler, true);
    }
    if (applePayContainer) {
      applePayContainer.addEventListener('click', applePayHandler, true);
    }
    if (googlePayContainer) {
      googlePayContainer.addEventListener('click', googlePayHandler, true);
    }

    return () => {
      if (cashAppContainer) {
        cashAppContainer.removeEventListener('click', cashAppHandler, true);
      }
      if (applePayContainer) {
        applePayContainer.removeEventListener('click', applePayHandler, true);
      }
      if (googlePayContainer) {
        googlePayContainer.removeEventListener('click', googlePayHandler, true);
      }
    };
  }, [isDialogReady, open]);

  // Check if card form is ready when accordion is expanded and useNewCard is true
  useEffect(() => {
    if (cardAccordionExpanded && useNewCard) {
      // Check card readiness with a delay to allow form to initialize
      const checkCardReady = () => {
        try {
          if (squarePaymentRef?.current) {
            const ready = squarePaymentRef.current?.isReady?.();
            setIsCardFormReady(ready || false);
            return ready;
          }
          return false;
        } catch (e) {
          setIsCardFormReady(false);
          return false;
        }
      };

      // Check after a short delay to allow DOM to settle
      const timer1 = setTimeout(() => {
        if (checkCardReady()) {
          return; // Already ready
        }
      }, 300);

      // Also check periodically in case it takes longer to initialize
      const interval = setInterval(() => {
        if (checkCardReady()) {
          clearInterval(interval);
        }
      }, 500);
      
      return () => {
        clearTimeout(timer1);
        clearInterval(interval);
      };
    } else {
      setIsCardFormReady(false);
    }
  }, [cardAccordionExpanded, useNewCard, squarePaymentRef]);

  const handleCardAccordionChange = useCallback((event, isExpanded) => {
    setCardAccordionExpanded(isExpanded);
    if (isExpanded) {
      setLocalPaymentMethod('CARD');
      setLocalSourceId(null);
      onCardOptionSelect?.();
    } else {
      // Reset payment method when accordion is closed
      setLocalPaymentMethod('');
      setLocalSourceId(null);
    }
  }, [onCardOptionSelect]);

  // Amount to charge wallet methods (cents). Mirrors OrderSummary: gift card → remainingAmount;
  // if API omits totalWithTip (e.g. no tip), fall back to total.
  const orderTotal = useMemo(() => {
    const p = apiPricingData;
    if (!p) return 0;

    const num = (v) => {
      if (v === undefined || v === null || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const giftBalance = num(p.giftCardBalance) || 0;
    const remaining = num(p.remainingAmount);

    if (giftBalance > 0 && remaining != null && remaining >= 0) {
      return remaining;
    }

    const twt = num(p.totalWithTip);
    const tot = num(p.total);

    if (twt != null && twt > 0) return twt;
    if (tot != null && tot > 0) return tot;
    if (twt != null) return twt;
    if (tot != null) return tot;
    return 0;
  }, [apiPricingData]);

  // Memoize store ID
  const effectiveStoreId = useMemo(
    () => storeIdD || user?.defaultStore,
    [storeIdD, user?.defaultStore]
  );

  // Auto-proceed to checkout after successful tokenization
  const proceedToCheckout = useCallback(
    (method, sourceIdValue, billingData) => {
      console.log('🚀 [PaymentMethodDialog] proceedToCheckout called:', { method, sourceIdValue, hasBillingData: !!billingData });

      // Show fullscreen loading immediately so user sees feedback when dialog closes
      // (for card, GP, Apple Pay, Cash App: ensures no gap between dialog close and checkout loading)
      onShowLoading?.();

      // Update parent state first - use functional updates to ensure state is set
      setPaymentMethod(method);
      setSourceId(sourceIdValue);
      
      // Also update local state
      setLocalPaymentMethod(method);
      setLocalSourceId(sourceIdValue);

      // Close dialog first
      onClose();

      // Use a small delay to ensure state updates and dialog close are processed
      // Then automatically call the confirm callback - this will fire the checkout function
      setTimeout(() => {
        console.log('🚀 [PaymentMethodDialog] Calling onConfirm for auto-checkout:', { method, sourceIdValue });
        if (onConfirm) {
          onConfirm(method, sourceIdValue, billingData);
        }
      }, 100);
    },
    [onConfirm, onClose, onShowLoading, setPaymentMethod, setSourceId]
  );

  // Memoized callbacks for payment methods to prevent re-renders
  const handleCashAppToken = useCallback(
    (token) => {
      setLocalPaymentMethod('CASH_APP');
      setLocalSourceId(token);
      setErr('');
      setCardAccordionExpanded(false);
      // Auto-proceed to checkout after successful tokenization
      proceedToCheckout('CASH_APP', token);
    },
    [setErr, proceedToCheckout]
  );

  const handleCashAppError = useCallback(
    (error) => {
      // Always log errors to console for debugging
      console.error('❌ [PaymentMethodDialog] Cash App Pay error:', error);
      
      // Check if this error is from initialization (before user interaction)
      // Errors from initialization typically include "not available", "not enabled", etc.
      const isInitError = error?.message && (
        error.message.toLowerCase().includes('not available') ||
        error.message.toLowerCase().includes('not enabled') ||
        error.message.toLowerCase().includes('sandbox mode') ||
        error.message.toLowerCase().includes('failed to initialize') ||
        error.message.toLowerCase().includes('not configured') ||
        error.message.toLowerCase().includes('merchant')
      );
      
      // Only show errors to user if they occurred after a user action (e.g., clicking payment button)
      // Initialization errors should never be shown to the user - they should only be logged
      if (isInitError) {
        // Log but don't show to user - this is an initialization/automatic error
        console.log('ℹ️ [PaymentMethodDialog] Cash App Pay error (not shown to user - initialization/automatic error):', error?.message || error);
        setLocalSourceId(null);
        return;
      }
      
      // If not an init error, assume it's from user action (tokenization, authorization, etc.)
      // Show the error to the user
      
      // Provide user-friendly error messages for user-initiated errors
      let friendlyErrorMessage = 'Cash App Pay authorization failed. Please try again or use a different payment method.';
      
      if (error?.message) {
        const errorMessage = error.message.toLowerCase();
        
        // Handle "Tokenization failed" specifically
        if (errorMessage.includes('tokenization failed') || errorMessage.includes('tokenization')) {
          friendlyErrorMessage = 'We couldn\'t process your Cash App payment. Please try scanning the QR code again or use a different payment method.';
        } else if (errorMessage.includes('token')) {
          friendlyErrorMessage = 'We couldn\'t process your Cash App payment. Please try scanning the QR code again or use a different payment method.';
        } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
          friendlyErrorMessage = 'We couldn\'t connect to process your payment. Please check your internet connection and try again.';
        } else if (errorMessage.includes('declined') || errorMessage.includes('decline')) {
          friendlyErrorMessage = 'Your Cash App payment was declined. Please try a different payment method or contact Cash App support.';
        } else if (errorMessage.includes('cancelled') || errorMessage.includes('cancel')) {
          friendlyErrorMessage = 'Payment was cancelled. Please try again if you want to complete your order.';
        } else if (errorMessage.includes('timeout') || errorMessage.includes('expired')) {
          friendlyErrorMessage = 'The payment authorization timed out. Please try scanning the QR code again.';
        }
      }
      
      setErr(friendlyErrorMessage);
      setLocalSourceId(null);
    },
    [setErr]
  );

  const handleApplePayToken = useCallback(
    (token) => {
      setLocalPaymentMethod('APPLE_PAY');
      setLocalSourceId(token);
      setErr('');
      setCardAccordionExpanded(false);
      // Auto-proceed to checkout after successful tokenization
      proceedToCheckout('APPLE_PAY', token);
    },
    [setErr, proceedToCheckout]
  );

  const handleApplePayError = useCallback(
    (error) => {
      // Always log errors to console for debugging
      console.error('❌ [PaymentMethodDialog] Apple Pay error:', error);
      
      // Check if this error is from initialization (before user interaction)
      // Errors from initialization typically include "not available", "not enabled", etc.
      const isInitError = error?.message && (
        error.message.toLowerCase().includes('not available') ||
        error.message.toLowerCase().includes('not enabled') ||
        error.message.toLowerCase().includes('failed to initialize') ||
        error.message.toLowerCase().includes('not configured') ||
        error.message.toLowerCase().includes('merchant')
      );
      
      // Only show errors to user if they occurred after a user action (e.g., clicking payment button)
      // Initialization errors should never be shown to the user - they should only be logged
      if (isInitError) {
        // Log but don't show to user - this is an initialization/automatic error
        console.log('ℹ️ [PaymentMethodDialog] Apple Pay error (not shown to user - initialization/automatic error):', error?.message || error);
        setLocalSourceId(null);
        return;
      }
      
      // If not an init error, assume it's from user action (tokenization, authorization, etc.)
      // Show the error to the user
      
      // Provide user-friendly error messages for user-initiated errors
      let friendlyErrorMessage = 'Apple Pay authorization failed. Please try again or use a different payment method.';
      
      if (error?.message) {
        const errorMessage = error.message.toLowerCase();
        
        // Handle "Tokenization failed" specifically
        if (errorMessage.includes('tokenization failed') || errorMessage.includes('tokenization')) {
          friendlyErrorMessage = 'We couldn\'t process your Apple Pay payment. Please try again or use a different payment method.';
        } else if (errorMessage.includes('token')) {
          friendlyErrorMessage = 'We couldn\'t process your Apple Pay payment. Please try again or use a different payment method.';
        } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
          friendlyErrorMessage = 'We couldn\'t connect to process your payment. Please check your internet connection and try again.';
        } else if (errorMessage.includes('declined') || errorMessage.includes('decline')) {
          friendlyErrorMessage = 'Your Apple Pay payment was declined. Please try a different payment method or contact your bank.';
        } else if (errorMessage.includes('cancelled') || errorMessage.includes('cancel')) {
          friendlyErrorMessage = 'Payment was cancelled. Please try again if you want to complete your order.';
        } else if (errorMessage.includes('not available') || errorMessage.includes('unavailable')) {
          friendlyErrorMessage = 'Apple Pay is not available. Please use a different payment method.';
        }
      }
      
      setErr(friendlyErrorMessage);
      setLocalSourceId(null);
    },
    [setErr]
  );

  const handleGooglePayToken = useCallback(
    (token) => {
      setLocalPaymentMethod('GOOGLE_PAY');
      setLocalSourceId(token);
      setErr('');
      setCardAccordionExpanded(false);
      // Auto-proceed to checkout after successful tokenization
      proceedToCheckout('GOOGLE_PAY', token);
    },
    [setErr, proceedToCheckout]
  );

  const handleGooglePayError = useCallback(
    (error) => {
      // Always log errors to console for debugging
      console.error('❌ [PaymentMethodDialog] Google Pay error:', error);
      
      // Check if this error is from initialization (before user interaction)
      // Errors from initialization typically include "not available", "not enabled", etc.
      const isInitError = error?.message && (
        error.message.toLowerCase().includes('not available') ||
        error.message.toLowerCase().includes('not enabled') ||
        error.message.toLowerCase().includes('failed to initialize') ||
        error.message.toLowerCase().includes('not configured')
      );
      
      // Only show errors to user if they occurred after a user action (e.g., clicking payment button)
      // Initialization errors should never be shown to the user - they should only be logged
      if (isInitError) {
        // Log but don't show to user - this is an initialization/automatic error
        console.log('ℹ️ [PaymentMethodDialog] Google Pay error (not shown to user - initialization/automatic error):', error?.message || error);
        setLocalSourceId(null);
        return;
      }
      
      // If not an init error, assume it's from user action (tokenization, authorization, etc.)
      // Show the error to the user
      
      // Provide user-friendly error messages for user-initiated errors
      let friendlyErrorMessage = 'Google Pay authorization failed. Please try again or use a different payment method.';
      
      if (error?.message) {
        const errorMessage = error.message.toLowerCase();
        
        // Handle "Tokenization failed" specifically
        if (errorMessage.includes('tokenization failed') || errorMessage.includes('tokenization')) {
          friendlyErrorMessage = 'We couldn\'t process your Google Pay payment. Please try again or use a different payment method.';
        } else if (errorMessage.includes('token')) {
          friendlyErrorMessage = 'We couldn\'t process your Google Pay payment. Please try again or use a different payment method.';
        } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
          friendlyErrorMessage = 'We couldn\'t connect to process your payment. Please check your internet connection and try again.';
        } else if (errorMessage.includes('declined') || errorMessage.includes('decline')) {
          friendlyErrorMessage = 'Your Google Pay payment was declined. Please try a different payment method or contact your bank.';
        } else if (errorMessage.includes('cancelled') || errorMessage.includes('cancel')) {
          friendlyErrorMessage = 'Payment was cancelled. Please try again if you want to complete your order.';
        } else if (errorMessage.includes('not available') || errorMessage.includes('unavailable')) {
          friendlyErrorMessage = 'Google Pay is not available. Please use a different payment method.';
        }
      }
      
      setErr(friendlyErrorMessage);
      setLocalSourceId(null);
    },
    [setErr]
  );

  const handleConfirm = async () => {
    // Validate payment method selection
    if (!localPaymentMethod) {
      setErr('Please select a payment method');
      return;
    }

    let finalSourceId = localSourceId;

    // Validate sourceId for methods that require it
    // For CARD: tokenize if new card, or use saved card
    if (localPaymentMethod === 'CARD') {
      // If using saved card, set sourceId to empty string
      if (selectedSavedCardId && !useNewCard) {
        finalSourceId = '';
        // Auto-proceed to checkout for saved card
        proceedToCheckout(localPaymentMethod, finalSourceId);
        return;
      } else if (useNewCard) {
        // For new card, tokenize it now
        if (!squarePaymentRef?.current) {
          setErr('Card form is not ready. Please try again.');
          return;
        }

        const isReady = squarePaymentRef.current.isReady?.();
        if (!isReady) {
          setErr('Please complete card details');
          return;
        }

        try {
          // Tokenize the card to get the nonce
          setErr(''); // Clear any previous errors
          const token = await squarePaymentRef.current.tokenize();
          finalSourceId = token;
          setLocalSourceId(token);
          // Auto-proceed to checkout after successful tokenization (sourceId and type only)
          proceedToCheckout(localPaymentMethod, finalSourceId);
          return;
        } catch (tokenError) {
          console.error('❌ [PaymentMethodDialog] Card tokenization failed:', tokenError);
          
          // Provide user-friendly error messages
          let friendlyErrorMessage = 'Unable to process your card. Please check your card details and try again.';
          
          if (tokenError?.message) {
            const errorMessage = tokenError.message.toLowerCase();
            
            // Check for specific error types and provide friendly messages
            if (errorMessage.includes('invalid') || errorMessage.includes('invalid card')) {
              friendlyErrorMessage = 'The card information you entered is invalid. Please check your card number, expiration date, CVV, and zip code.';
            } else if (errorMessage.includes('expired') || errorMessage.includes('expiration')) {
              friendlyErrorMessage = 'Your card has expired. Please use a different card or check the expiration date.';
            } else if (errorMessage.includes('cvv') || errorMessage.includes('security code')) {
              friendlyErrorMessage = 'The security code (CVV) is incorrect. Please check the 3-digit code on the back of your card.';
            } else if (errorMessage.includes('zip') || errorMessage.includes('postal')) {
              friendlyErrorMessage = 'The zip code you entered is incorrect. Please check your billing zip code.';
            } else if (errorMessage.includes('declined') || errorMessage.includes('decline')) {
              friendlyErrorMessage = 'Your card was declined. Please try a different payment method or contact your bank.';
            } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
              friendlyErrorMessage = 'We couldn\'t connect to process your payment. Please check your internet connection and try again.';
            } else if (errorMessage.includes('tokenization failed') || errorMessage.includes('tokenization')) {
              friendlyErrorMessage = 'We couldn\'t process your card. Please verify all card details are correct and try again.';
            } else if (errorMessage.includes('token')) {
              friendlyErrorMessage = 'We couldn\'t process your card. Please verify all card details are correct and try again.';
            }
          }
          
          setErr(friendlyErrorMessage);
          return;
        }
      } else {
        setErr('Please select a payment method');
        return;
      }
    } else if (localPaymentMethod === 'CASH_APP' && !localSourceId) {
      setErr('Please complete Cash App setup');
      return;
    } else if (localPaymentMethod === 'APPLE_PAY' && !localSourceId) {
      setErr('Please complete Apple Pay setup');
      return;
    } else if (localPaymentMethod === 'GOOGLE_PAY' && !localSourceId) {
      setErr('Please complete Google Pay setup');
      return;
    }

    // For other payment methods that already have sourceId, auto-proceed
    proceedToCheckout(localPaymentMethod, finalSourceId);
  };

  const handleClose = () => {
    setLocalPaymentMethod(paymentMethod || '');
    setLocalSourceId(sourceId || null);
    setErr('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '24px',
          padding: isMobile ? 0 : '8px',
          maxWidth: isMobile ? '100%' : '600px',
          margin: isMobile ? 0 : '32px auto',
          maxHeight: isMobile ? '100%' : '90vh',
          boxShadow: isMobile 
            ? 'none' 
            : '0px 20px 60px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 1.5 : 2,
          paddingBottom: isMobile ? '8px' : '12px',
          paddingTop: isMobile ? '16px' : '24px',
          paddingX: isMobile ? '16px' : '24px',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Box
          sx={{
            width: isMobile ? '40px' : '48px',
            height: isMobile ? '40px' : '48px',
            borderRadius: isMobile ? '10px' : '12px',
            backgroundColor: `${environment?.mainColor}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: isMobile ? '20px' : '24px',
              color: environment?.mainColor,
              fontWeight: 700,
            }}
          >
            <span role="img" aria-label="Payment">💳</span>
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: 700,
            color: '#172B4D',
            fontFamily: 'Jost, sans-serif',
            lineHeight: 1.2,
          }}
        >
          Select Payment Method
        </Typography>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          padding: isMobile ? '16px' : '24px',
          marginTop: isMobile ? '8px' : '16px',
          paddingBottom: isMobile ? '16px' : '24px',
        }}
      >
        <Stack spacing={isMobile ? 1.5 : 2}>
          {/* Card Payment Option - Accordion */}
          <Accordion
            expanded={cardAccordionExpanded}
            onChange={handleCardAccordionChange}
            sx={{
              boxShadow: '0 2px 12px 0 rgba(16, 30, 54, 0.06)',
              borderRadius: '12px !important',
              border: cardAccordionExpanded
                ? `2px solid ${environment.mainColor}`
                : '2px solid #E5E7EB',
              '&:before': {
                display: 'none',
              },
              '&.Mui-expanded': {
                margin: 0,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: environment.mainColor, fontSize: isMobile ? '20px' : '24px' }} />}
              sx={{
                padding: isMobile ? '12px 16px' : '16px 20px',
                minHeight: isMobile ? '48px' : '56px',
                '&.Mui-expanded': {
                  minHeight: isMobile ? '48px' : '56px',
                },
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                  '&.Mui-expanded': {
                    margin: 0,
                  },
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1.5 : 2, width: '100%' }}>
                <Box
                  sx={{
                    width: isMobile ? '40px' : '48px',
                    height: isMobile ? '26px' : '32px',
                    borderRadius: isMobile ? '5px' : '6px',
                    background:
                      'linear-gradient(135deg, #EB001B 0%, #F79E1B 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: isMobile ? '12px' : '16px',
                      height: isMobile ? '12px' : '16px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      marginRight: isMobile ? '-6px' : '-8px',
                    }}
                  />
                  <Box
                    sx={{
                      width: isMobile ? '12px' : '16px',
                      height: isMobile ? '12px' : '16px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Jost',
                    fontSize: isMobile ? '15px' : '16px',
                    fontWeight: 600,
                    color: '#172B4D',
                  }}
                >
                  Credit/Debit Card
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: isMobile ? '16px' : '20px' }}>
              <PaymentsDialog
                orderType={orderType}
                cards={cards}
                setCards={setCards}
                handleChange={handleChange}
                onSelect={onSelect}
                change={change}
                openCredit={openCredit}
                setCredit={setCredit}
                setSelectedCard={setSelectedCard}
                cash={cash}
                setCash={setCash}
                sourceId={localSourceId}
                setSourceId={setLocalSourceId}
                squarePaymentRef={squarePaymentRef}
                savedCards={savedCards}
                selectedSavedCardId={selectedSavedCardId}
                onSavedCardSelect={onSavedCardSelect}
                shouldSaveCard={shouldSaveCard}
                onSaveCardChange={onSaveCardChange}
                useNewCard={useNewCard}
                onUseNewCardChange={onUseNewCardChange}
              />
            </AccordionDetails>
          </Accordion>

          {/* Cash App Pay - Direct Square Button */}
          {isDialogReady && (
            <Box
              ref={cashAppContainerRef}
              key="cash-app-pay-container"
              sx={{
                padding: isMobile ? '12px 16px' : '16px 20px',
                backgroundColor: 'white',
                borderRadius: isMobile ? '10px' : '12px',
                border:
                  localPaymentMethod === 'CASH_APP'
                    ? `2px solid ${environment.mainColor}`
                    : '2px solid #E5E7EB',
                transition: 'all 0.2s ease',
                minHeight: isMobile ? '70px' : '80px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                // Prevent container from being clickable
                '&:not(:has(#sq-cash-app-pay-container))': {
                  pointerEvents: 'none',
                },
                // Ensure Square SDK button container doesn't fill entire area
                '& #sq-cash-app-pay-container': {
                  width: 'auto !important',
                  maxWidth: '100%',
                  display: 'inline-block',
                  margin: '0 auto',
                },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <MemoizedCashAppPay
                  ref={cashAppPayRef}
                  orderTotal={orderTotal}
                  onToken={handleCashAppToken}
                  onError={handleCashAppError}
                  currentStoreId={effectiveStoreId}
                  user={user}
                  isVisible={open && isDialogReady}
                />
              </Box>
              {localPaymentMethod === 'CASH_APP' && localSourceId && (
                <Fade in={true} timeout={300}>
                  <Box
                    sx={{
                      mt: isMobile ? 1.5 : 2,
                      p: isMobile ? 1.5 : 2,
                      backgroundColor: '#F0FDF4',
                      borderRadius: isMobile ? '6px' : '8px',
                      border: '1px solid #86EFAC',
                      width: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Jost',
                        fontSize: isMobile ? '13px' : '14px',
                        color: '#166534',
                        fontWeight: 600,
                        textAlign: 'center',
                      }}
                    >
                      <span role="img" aria-label="Success">✅</span> Payment Authorized
                    </Typography>
                  </Box>
                </Fade>
              )}
            </Box>
          )}

          {/* Apple Pay - Direct Square Button */}
          {isDialogReady && (
            <Box
              ref={applePayContainerRef}
              key="apple-pay-container"
              sx={{
                padding: isMobile ? '12px 16px' : '16px 20px',
                backgroundColor: 'white',
                borderRadius: isMobile ? '10px' : '12px',
                border:
                  localPaymentMethod === 'APPLE_PAY'
                    ? `2px solid ${environment.mainColor}`
                    : '2px solid #E5E7EB',
                transition: 'all 0.2s ease',
                minHeight: isMobile ? '70px' : '80px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                // Ensure only buttons are clickable, not the container
                '& button': {
                  pointerEvents: 'auto',
                },
                '& > *:not(button)': {
                  pointerEvents: 'none',
                },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <MemoizedApplePay
                  ref={applePayRef}
                  orderTotal={orderTotal}
                  onToken={handleApplePayToken}
                  onError={handleApplePayError}
                  currentStoreId={effectiveStoreId}
                  user={user}
                  isVisible={open && isDialogReady}
                />
              </Box>
            </Box>
          )}

          {/* Google Pay - Direct Square Button */}
          {isDialogReady && (
            <Box
              ref={googlePayContainerRef}
              key="google-pay-container"
              sx={{
                padding: isMobile ? '12px 16px' : '16px 20px',
                backgroundColor: 'white',
                borderRadius: isMobile ? '10px' : '12px',
                border:
                  localPaymentMethod === 'GOOGLE_PAY'
                    ? `2px solid ${environment.mainColor}`
                    : '2px solid #E5E7EB',
                transition: 'all 0.2s ease',
                minHeight: isMobile ? '70px' : '80px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                // Prevent container from being clickable
                '&:not(:has(#sq-google-pay-container))': {
                  pointerEvents: 'none',
                },
                // Ensure Square SDK button container doesn't fill entire area
                '& #sq-google-pay-container': {
                  width: 'auto !important',
                  maxWidth: '100%',
                  display: 'inline-block',
                  margin: '0 auto',
                },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <MemoizedGooglePay
                  ref={googlePayRef}
                  orderTotal={orderTotal}
                  onToken={handleGooglePayToken}
                  onError={handleGooglePayError}
                  currentStoreId={effectiveStoreId}
                  user={user}
                  handleGooglePaySuccess={handleGooglePaySuccess}
                  isVisible={open && isDialogReady}
                />
              </Box>
            </Box>
          )}
        </Stack>

        {err && (
          <Box
            sx={{
              mt: isMobile ? 1.5 : 2,
              p: isMobile ? 1.5 : 2,
              backgroundColor: '#FEF2F2',
              borderRadius: isMobile ? '6px' : '8px',
              border: '1px solid #FECACA',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Jost',
                fontSize: isMobile ? '13px' : '14px',
                color: '#DC2626',
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              {err}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: isMobile ? '12px 16px' : '16px 24px',
          borderTop: '1px solid #E5E7EB',
          gap: isMobile ? 1.5 : 2,
          flexDirection: isMobile ? 'column-reverse' : 'row',
          '& > :not(style) ~ :not(style)': {
            marginLeft: isMobile ? 0 : undefined,
            marginTop: isMobile ? 0 : undefined,
            marginBottom: isMobile ? '8px' : undefined,
          },
        }}
      >
        <Button
          onClick={handleClose}
          fullWidth={isMobile}
          sx={{
            textTransform: 'capitalize',
            color: '#6B7280',
            fontFamily: 'Jost',
            fontWeight: 600,
            fontSize: isMobile ? '15px' : '16px',
            padding: isMobile ? '12px 24px' : '10px 24px',
            minHeight: isMobile ? '48px' : 'auto',
          }}
        >
          Cancel
        </Button>
        {/* Only show Confirm Payment button for CARD payments */}
        {/* Cash App, Apple Pay, and Google Pay auto-proceed on successful tokenization */}
        {localPaymentMethod === 'CARD' && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            fullWidth={isMobile}
            disabled={
              !localPaymentMethod ||
              (!(selectedSavedCardId && !useNewCard) && 
               !(useNewCard && isCardFormReady))
            }
            sx={{
              textTransform: 'capitalize',
              backgroundColor: environment.mainColor,
              fontFamily: 'Jost',
              fontWeight: 600,
              borderRadius: isMobile ? '8px' : '8px',
              boxShadow: 'none',
              padding: isMobile ? '12px 24px' : '10px 24px',
              fontSize: isMobile ? '15px' : '16px',
              minHeight: isMobile ? '48px' : 'auto',
              '&:hover': {
                backgroundColor: environment.mainColor,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF',
              },
            }}
          >
            Confirm Payment
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentMethodDialog;
